import { IconCheck } from "@tabler/icons-react";
import { Button, Flex, Skeleton, Space, Tag, Typography, theme } from "antd";
import type { ReactNode } from "react";
import {
	useOpenStripeBillingPortal,
	useOrganizationAWSMarketplaceSubscription,
	useOrganizationMetrics,
	useOrganizationSubscription,
	useStartStripeCheckout,
} from "@/common/artemis-operations/organization";
import { useNotify } from "@/common/use-notify";
import { BaseContainer, CustomIcon, QueryGuard } from "@/components/base";

// Statuses where the org is (or is still trying to be) paying — mirrors LiveStatuses in
// stripe-billing-service.cs, which refuses a second checkout for these.
const LIVE_STATUSES = [
	"active",
	"trialing",
	"past_due",
	"incomplete",
	"unpaid",
];

// Only states that need the admin's attention get a tag; "active" is the plan working as expected.
const STATUS_TAG: Record<string, { label: string; color: string }> = {
	trialing: { label: "Trial", color: "blue" },
	past_due: { label: "Payment past due", color: "orange" },
	incomplete: { label: "Payment pending", color: "orange" },
	unpaid: { label: "Unpaid", color: "red" },
};

// What the paid plan offers, shown to orgs that don't have it yet.
const PAID_PLAN_POINTS = [
	"Buy environment quota at a fixed monthly price per environment",
	"Choose how many environments you need at checkout",
	"Add or remove environments, or cancel, at any time",
];

const plural = (n: number) => (n === 1 ? "environment" : "environments");

// AWS Marketplace contracts are managed by the buyer in their own AWS console, not by us.
const AWS_SUBSCRIPTIONS_URL =
	"https://console.aws.amazon.com/marketplace/home#/subscriptions";

/** One paid subscription: where it's billed, what it buys, and where to manage it. */
function SubscriptionRow({
	source,
	environments,
	tag,
	action,
}: {
	source: string;
	environments: number;
	tag?: { label: string; color: string };
	action: ReactNode;
}) {
	const { token } = theme.useToken();
	return (
		<Flex
			align="center"
			justify="space-between"
			gap={16}
			wrap
			style={{
				width: "100%",
				padding: token.paddingSM,
				border: `1px solid ${token.colorBorderSecondary}`,
				borderRadius: token.borderRadiusLG,
			}}
		>
			<Flex align="center" gap={8}>
				<Tag>{source}</Tag>
				<Typography.Text strong>
					{environments} {plural(environments)}
				</Typography.Text>
				{tag && <Tag color={tag.color}>{tag.label}</Tag>}
			</Flex>
			{action}
		</Flex>
	);
}

/**
 * Org-admin plan section. An org can pay through Stripe, AWS Marketplace, or both; each purchase
 * adds environments on top of the free base. With a subscription it lists each one with its source
 * and where to manage it (Stripe Billing Portal, or the buyer's AWS console). Without a Stripe one,
 * and when Stripe billing is on, it explains the paid plan and offers Upgrade (Stripe Checkout,
 * where the buyer picks the quantity). Rendered on the admin-gated Billing page; the mutations are
 * org-admin-only server-side.
 */
export function OrganizationBilling({
	stripeEnabled,
}: {
	/** Whether Stripe billing is on (Artemis:Stripe:Enabled); off hides Upgrade. */
	stripeEnabled: boolean;
}) {
	const notify = useNotify();
	const { token } = theme.useToken();
	const stripeQuery = useOrganizationSubscription();
	const awsQuery = useOrganizationAWSMarketplaceSubscription();
	const subscription = stripeQuery.data;
	const awsSubscription = awsQuery.data;
	const isPending = stripeQuery.isPending || awsQuery.isPending;
	const isError = stripeQuery.isError || awsQuery.isError;
	const { data: metrics } = useOrganizationMetrics();
	const checkout = useStartStripeCheckout();
	const portal = useOpenStripeBillingPortal();

	const isLive = !!subscription && LIVE_STATUSES.includes(subscription.status);
	const tag = isLive ? STATUS_TAG[subscription.status] : undefined;
	const onError = (err: unknown) =>
		notify.error((err as Error).message || "Couldn't reach Stripe.");

	// Without a subscription the environment cap is the plan default; null means unlimited.
	const includedEnvironments = metrics?.maxEnvironments;

	const manageButton = subscription && (
		<Button
			loading={portal.isPending}
			onClick={() => portal.mutate(undefined, { onError })}
		>
			Manage billing
		</Button>
	);

	return (
		<BaseContainer title="Plan">
			<QueryGuard
				isPending={isPending}
				isError={isError}
				isNull={false}
				pending={<Skeleton active paragraph={{ rows: 3 }} />}
			>
				<Flex vertical gap={16} align="flex-start">
					{(isLive || awsSubscription) && (
						<Flex vertical gap={8} style={{ width: "100%" }}>
							<Typography.Text>
								{metrics?.maxEnvironments != null
									? `Your organization can have up to ${metrics.maxEnvironments} ${plural(metrics.maxEnvironments)}: the environments included for free, plus the ones you pay for below.`
									: "The environments you pay for below come on top of the ones included for free."}
							</Typography.Text>
							{isLive && (
								<SubscriptionRow
									source="Stripe"
									environments={subscription.environments}
									tag={tag}
									action={manageButton}
								/>
							)}
							{awsSubscription && (
								<SubscriptionRow
									source="AWS Marketplace"
									environments={awsSubscription.environments}
									action={
										<Button
											href={AWS_SUBSCRIPTIONS_URL}
											target="_blank"
											rel="noopener noreferrer"
										>
											Manage in AWS
										</Button>
									}
								/>
							)}
							{isLive && (
								<Typography.Text
									type="secondary"
									style={{ fontSize: token.fontSizeSM }}
								>
									To add or remove Stripe environments, update the quantity in
									Manage billing.
								</Typography.Text>
							)}
						</Flex>
					)}
					{stripeEnabled && !isLive && (
						<>
							<Flex vertical gap={4}>
								<Typography.Text strong style={{ fontSize: token.fontSizeLG }}>
									Need more environments?
								</Typography.Text>
								<Typography.Text>
									{!awsSubscription && includedEnvironments != null
										? `Your organization can currently have up to ${includedEnvironments} ${plural(includedEnvironments)}. `
										: ""}
									A paid plan adds the number of environments you buy.
								</Typography.Text>
							</Flex>
							<Flex
								vertical
								gap={8}
								style={{
									width: "100%",
									padding: token.paddingMD,
									background: token.colorFillAlter,
									border: `1px solid ${token.colorBorderSecondary}`,
									borderRadius: token.borderRadiusLG,
								}}
							>
								{PAID_PLAN_POINTS.map((point) => (
									<Flex key={point} align="center" gap={8}>
										<CustomIcon
											icon={IconCheck}
											size={18}
											color={token.colorSuccess}
										/>
										<Typography.Text>{point}</Typography.Text>
									</Flex>
								))}
							</Flex>
							<Flex align="center" gap={16} wrap>
								<Space>
									<Button
										type="primary"
										loading={checkout.isPending}
										onClick={() => checkout.mutate(undefined, { onError })}
									>
										Upgrade
									</Button>
									{manageButton}
								</Space>
								<Typography.Text
									type="secondary"
									style={{ fontSize: token.fontSizeSM }}
								>
									Secure checkout by Stripe. The price is shown before you pay.
								</Typography.Text>
							</Flex>
						</>
					)}
				</Flex>
			</QueryGuard>
		</BaseContainer>
	);
}
